<script setup>
// Admin "Waiting to be placed" card on ladder / league cycle / americano pages
// (JOIN_SETTINGS_PLAN.md §7.6): the waiting rows with their partner picks, suggested
// teams (mutual picks first), select-to-form-a-team, Place and Dismiss. Opens
// expanded from ?place=1 (the admin push / Home card deep link).
import UserProfilePic from '~/components/users/UserProfilePic.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import PlacePlayersDialog from '~/components/placement/PlacePlayersDialog.vue';
import {usePendingPlacements} from '~/composables/usePendingPlacements';
import {useAmericanoFormat} from '~/composables/useAmericanoFormat';

const props = defineProps({
  referenceType: {type: Number, required: true},
  referenceId: {type: String, required: true},
  clubId: {type: String, required: true},
  playersPerTeam: {type: Number, default: 1},
  // From the entity response — the card only fetches once it has something to show.
  count: {type: Number, default: 0},
  divisions: {type: Array, default: () => []},
  // Ladder only: the current order, so the admin can slot a placement in.
  ladderEntries: {type: Array, default: () => []},
  handicapsEnabled: {type: Boolean, default: false},
  // Teams already in the competition — see PlacePlayersDialog.
  existingTeams: {type: Array, default: () => []},
  allowMultipleTeams: {type: Boolean, default: false},
  manageRoute: {type: Object, default: null},
  setupHere: {type: Boolean, default: false},
  // Americano: the cycle's entry fee, passed through to the Give seat dialog (optional).
  entryFeeMinor: {type: Number, default: null},
  entryFeeCurrency: {type: String, default: null},
});
const emit = defineEmits(['changed', 'setup']);

const {items, listPending, listFor, suggestions, loadSuggestions, dismiss, actionPending, actionErrors} = usePendingPlacements();
const {setAlertMessage} = useAlertService();
const {moreToJoinText} = useAmericanoFormat();
const route = useRoute();
const router = useRouter();

const rootEl = ref(null);
const loaded = ref(false);
const selected = ref([]);
const placeOpen = ref(false);
const placeRows = ref([]);

const isAmericano = computed(() => props.referenceType === 6);
const isLadder = computed(() => props.referenceType === 1);
const teamSize = computed(() => (isAmericano.value ? 1 : Math.max(1, props.playersPerTeam)));
const waiting = computed(() => items.value ?? []);

const load = async () => {
  await listFor(props.referenceType, props.referenceId);
  if (teamSize.value > 1) await loadSuggestions(props.referenceType, props.referenceId);
  loaded.value = true;
  selected.value = selected.value.filter(id => waiting.value.some(w => w.id === id));
};

watch(() => [props.referenceId, props.count], () => {
  if (props.count > 0 || loaded.value) load();
}, {immediate: true});

// Deep link: ?place=1 scrolls the card into view, then drops the flag so a refresh
// doesn't keep jumping.
onMounted(() => {
  if (route.query.place === '1') {
    // After the first list load, so the card has its final height before it scrolls into view.
    const stop = watch(loaded, (v) => {
      if (!v) return;
      setTimeout(() => rootEl.value?.scrollIntoView({behavior: 'smooth', block: 'start'}), 150);
      stop();
    }, {immediate: true});
    const {place, ...rest} = route.query;
    router.replace({query: rest});
  }
});

const toggle = (row) => {
  if (teamSize.value === 1) return;
  selected.value = selected.value.includes(row.id)
      ? selected.value.filter(id => id !== row.id)
      : selected.value.length < teamSize.value ? [...selected.value, row.id] : selected.value;
};

const openPlace = (rows) => {
  placeRows.value = rows;
  placeOpen.value = true;
};

const placeSelected = () => openPlace(waiting.value.filter(w => selected.value.includes(w.id)));
const placeGroup = (group) => openPlace(waiting.value.filter(w => group.pendingPlacementIds.includes(w.id)));

const onPlaced = async () => {
  selected.value = [];
  await load();
  emit('changed');
};

const onDismiss = async (row) => {
  if (await dismiss(row.id)) {
    setAlertMessage(`${row.fullName} removed from the waiting area`);
    await load();
    emit('changed');
  } else {
    setAlertMessage({
      message: actionErrors.value?.global?.[0] || `Couldn't remove ${row.fullName} from the waiting area. Please try again`,
      color: 'error',
    });
  }
};

const joinedAgo = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'joined today';
  if (days === 1) return 'joined yesterday';
  return `joined ${days} days ago`;
};

const groups = computed(() => suggestions.value?.groups ?? []);
const reasonClass = (g) => g.reason === 0 ? 'tl-chip--ok' : g.reason === 1 ? 'tl-chip--brand' : '';
const placeLabel = computed(() => isAmericano.value ? 'Give seat' : 'Place');
const title = computed(() => {
  const n = waiting.value.length || props.count;
  return n === 1 ? '1 player waiting to be placed' : `${n} players waiting to be placed`;
});
const helper = computed(() => {
  if (isAmericano.value) {
    // Americano seats only come in fours (a division of 4, or 4 → 8 → 12), so a few
    // waiting players cannot simply be added. Say what is actually possible right now.
    const n = waiting.value.length || props.count;
    const more = moreToJoinText(n);
    if (!props.divisions.length) {
      return n < 4
          ? `Divisions hold exactly 4, 8 or 12 players, so ${more} before divisions can be set up.`
          : 'Enough have joined to set up divisions. Tap Give seat to start. The waiting players are listed for you there.';
    }
    return n < 4
        ? `Give a player a seat by replacing someone in a division. Seats come in fours, so adding them without replacing anyone needs ${more}, or include them in the next cycle.`
        : 'Give a player a seat by replacing someone, or edit the divisions to add one or enlarge one for them.';
  }
  if (isLadder.value) {
    // Placing chooses the rung now, so the copy no longer promises the bottom.
    return teamSize.value > 1
        ? `Tap ${teamSize.value} players to form a team, then choose where it goes on the ladder.`
        : 'Place each player when you are ready, and choose where they go on the ladder.';
  }
  if (teamSize.value > 1) return `Tap ${teamSize.value} players to form a team, or place one with a club member as their team-mate.`;
  return 'Place each player into a division when you are ready.';
});
</script>

<template>
  <div v-if="count > 0 || waiting.length > 0" ref="rootEl" class="tl-card ppc my-4">
    <div class="ppc-head">
      <v-avatar size="42" color="warningTint">
        <v-icon color="warning">mdi-account-clock-outline</v-icon>
      </v-avatar>
      <div class="flex-fill min-w-0">
        <div class="ppc-title">{{ title }}</div>
        <div class="ppc-sub">{{ helper }}</div>
      </div>
      <v-btn v-if="teamSize > 1 && selected.length === teamSize" color="primary" variant="flat" size="small" rounded="pill"
             prepend-icon="mdi-check" @click="placeSelected">Place {{ teamSize }}</v-btn>
      <v-btn v-else-if="teamSize > 1 && selected.length === 1" color="primary" variant="outlined" size="small" rounded="pill"
             prepend-icon="mdi-account-plus" @click="placeSelected">Place with…</v-btn>
    </div>

    <!-- Suggested teams -->
    <div v-if="groups.length" class="ppc-suggest">
      <div class="tl-label mb-2">Suggested teams</div>
      <div class="ppc-groups">
        <div v-for="(g, i) in groups" :key="i" class="ppc-group">
          <div class="min-w-0">
            <div class="ppc-group-names">{{ g.playerNames.join(' & ') }}</div>
            <span class="tl-chip ppc-chip" :class="reasonClass(g)">
              <v-icon v-if="g.reason === 0" size="12" icon="mdi-heart" class="mr-1"/>{{ g.label }}
            </span>
          </div>
          <v-btn color="primary" variant="flat" size="small" rounded="pill" @click="placeGroup(g)">Place</v-btn>
        </div>
      </div>
    </div>

    <!-- Waiting rows -->
    <div class="ppc-rows">
      <v-skeleton-loader v-if="listPending && !loaded" type="list-item-avatar-two-line@2"/>
      <div v-for="row in waiting" :key="row.id" class="ppc-row"
           :class="{'ppc-row--selectable': teamSize > 1, 'ppc-row--on': selected.includes(row.id)}"
           @click="toggle(row)">
        <v-icon v-if="teamSize > 1" size="20"
                :icon="selected.includes(row.id) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline'"
                :color="selected.includes(row.id) ? 'primary' : 'textSubtle'"/>
        <user-profile-pic :user="{imageFileName: row.imageFileName, fullName: row.fullName}" size="small"/>
        <div class="flex-fill min-w-0">
          <div class="ppc-name">{{ row.fullName }}</div>
          <div class="ppc-meta">
            <span>{{ joinedAgo(row.createdAt) }}</span>
            <span v-if="row.source === 1"> · approved request</span>
            <span v-if="row.message" class="ppc-msg"> · “{{ row.message }}”</span>
          </div>
          <div v-if="row.preferredPartners?.length || row.preferredBy?.length" class="ppc-prefs">
            <span v-for="p in row.preferredPartners" :key="'p' + p.playerId" class="tl-chip ppc-chip"
                  :class="p.isMutual ? 'tl-chip--ok' : (p.stillWaiting ? 'tl-chip--brand' : '')">
              <v-icon v-if="p.isMutual" size="12" icon="mdi-heart" class="mr-1"/>
              Prefers {{ p.fullName }}{{ p.stillWaiting ? '' : ' (placed)' }}
            </span>
            <span v-for="p in (row.preferredBy ?? []).filter(b => !(row.preferredPartners ?? []).some(x => x.playerId === b.playerId))"
                  :key="'b' + p.playerId" class="tl-chip ppc-chip">
              Picked by {{ p.fullName }}
            </span>
          </div>
        </div>
        <div class="tl-rowactions" @click.stop>
          <v-btn v-if="teamSize === 1 && !(isAmericano && !divisions.length && waiting.length < 4)"
                 color="primary" variant="flat" size="small" rounded="pill" @click="openPlace([row])">
            {{ placeLabel }}
          </v-btn>
          <crud-confirm-dialog
              title="Remove from the waiting area?"
              :message="`${row.fullName} stays a club member but will no longer be waiting to join this competition.`"
              confirm-text="Remove"
              cancel-text="Keep waiting"
              @confirm="() => onDismiss(row)">
            <template #default="{click}">
              <tl-icon-btn tooltip="Remove from waiting area" icon="mdi-close" danger @click="click"/>
            </template>
          </crud-confirm-dialog>
        </div>
      </div>
      <div v-if="loaded && !waiting.length" class="ppc-empty">Everyone has been placed.</div>
    </div>

    <place-players-dialog
        v-model="placeOpen"
        :rows="placeRows"
        :waiting="waiting"
        :reference-type="referenceType"
        :reference-id="referenceId"
        :club-id="clubId"
        :players-per-team="teamSize"
        :divisions="divisions"
        :ladder-entries="ladderEntries"
        :handicaps-enabled="handicapsEnabled"
        :existing-teams="existingTeams"
        :allow-multiple-teams="allowMultipleTeams"
        :manage-route="manageRoute"
        :setup-here="setupHere"
        :entry-fee-minor="entryFeeMinor"
        :entry-fee-currency="entryFeeCurrency"
        @placed="onPlaced"
        @setup="emit('setup')"
    />
  </div>
</template>

<style scoped lang="scss">
.ppc {
  overflow: hidden;
}

.ppc-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.ppc-title {
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
}

.ppc-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

.ppc-suggest {
  padding: 0 16px 12px;
}

.ppc-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ppc-group {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 8px 12px;
  border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-light));
  flex: 1 1 260px;
  min-width: 0;

  .ppc-group-names {
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > .min-w-0 {
    flex: 1 1 auto;
  }
}

.ppc-rows {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.ppc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  &--selectable {
    cursor: pointer;

    &:hover {
      background: rgb(var(--v-theme-light));
    }
  }

  &--on {
    background: rgb(var(--v-theme-brandTint));
  }
}

.ppc-name {
  font-size: 13.5px;
  font-weight: 600;
}

.ppc-meta {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.ppc-msg {
  font-style: italic;
}

.ppc-prefs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.ppc-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  padding: 2px 8px;
}

.ppc-empty {
  padding: 14px 16px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
